import { type Experiment, ExperimentStatusKind } from '@gonfalon/experiments/types';
import type { FeatureFlag } from '@gonfalon/flags/types';
import { Message } from '@gonfalon/messages/Message';
import { toFlagTargeting } from '@gonfalon/navigator/navigators';
import { Alert, Heading, Link } from '@launchpad-ui/components';

import styles from './ExperimentApprovalModal.module.css';

type ExperimentIterationStatus = NonNullable<Experiment['currentIteration']>['status'];

type ExperimentPendingApprovalAlertsProps = {
  flagData?: FeatureFlag;
  environmentKey?: string;
  flagKey?: string;
  projectKey?: string;
  hasPendingApproval?: boolean;
  experimentCurrentIterationStatus?: ExperimentIterationStatus;
};

export function ExperimentPendingApprovalAlerts({
  flagData,
  flagKey,
  environmentKey,
  projectKey,
  hasPendingApproval,
  experimentCurrentIterationStatus,
}: ExperimentPendingApprovalAlertsProps) {
  const flagTargetingLink =
    flagData && flagKey && environmentKey && projectKey
      ? {
          href: toFlagTargeting({ projectKey, flagKey, environmentKey }),
          label: flagData.name || flagKey,
        }
      : undefined;
  const isStopped = experimentCurrentIterationStatus === ExperimentStatusKind.STOPPED;

  if (hasPendingApproval && isStopped && flagTargetingLink) {
    return (
      <Alert status="warning">
        <Heading>
          <Message id="experiments.pendingApprovalAlert.conflictHeading" />
        </Heading>
        <p>
          <Message id="experiments.pendingApprovalAlert.stoppedConflictBody" />
        </p>
        <ul className={styles.alertList}>
          <li>
            <Message id="experiments.pendingApprovalAlert.checkPendingApprovals" />
          </li>
          <li>
            <Message
              id="experiments.pendingApprovalAlert.checkFlagChanges"
              values={{
                flagName: flagTargetingLink.label,
                link: (chunks) => <Link href={flagTargetingLink.href}>{chunks}</Link>,
              }}
            />
          </li>
        </ul>
      </Alert>
    );
  }

  if (!hasPendingApproval && isStopped && flagTargetingLink) {
    return (
      <Alert status="info">
        <Heading>
          <Message id="experiments.pendingApprovalAlert.flagChangesHeading" />
        </Heading>
        <p>
          <Message
            id="experiments.pendingApprovalAlert.flagChangesBody"
            values={{
              flagName: flagTargetingLink.label,
              link: (chunks) => <Link href={flagTargetingLink.href}>{chunks}</Link>,
            }}
          />
        </p>
      </Alert>
    );
  }

  if (hasPendingApproval) {
    return (
      <Alert status="warning">
        <Heading>
          <Message id="experiments.pendingApprovalAlert.conflictHeading" />
        </Heading>
        <p>
          <Message id="experiments.pendingApprovalAlert.conflictBody" />
        </p>
      </Alert>
    );
  }

  return null;
}
